import firebase from '../store/firebase';
import axios from 'axios';
import { FB_URL } from '../store/actions/types';
import generalEval from '../components/UserProfile/data/generalEval.json';

/**
 * Validates a lead against content standards (temporarily disabled company values filter)
 * @param {Object} lead - The lead data being submitted
 * @param {string} companyID - The ID of the company
 * @returns {Promise<Object>} - Promise resolving to { valid: boolean, message: string }
 */
export const validateLeadAgainstCompanyValues = async (lead, companyID) => {
  try {
    // First, check if OpenAI is working
    const openAIStatus = await checkOpenAIStatus();
    
    if (!openAIStatus.working) {
      return {
        valid: true,
        message: 'OpenAI API is not available. Your lead will be submitted without validation.',
        apiStatus: 'openai_unavailable',
        openAIStatus: openAIStatus
      };
    }
    
    // CLIENT REQUEST: Temporarily disable company values filter
    // Instead, filter out obscene/swearing and senseless posts
    
    /* COMMENTED OUT - Company Values Filtering (as requested by client)
    // Use the specific test company ID as requested by client
    // This company has values in generalEval.json:
    // 1. Leading Self
    // 2. Leading Others
    // 3. Leading Organisation
    // 4. Leading Society
    const testCompanyID = "-KgsUcgfo7z1U9MXgd9i";
    
    // Get company values from generalEval.json for the test company
    // This uses the values from the JSON file instead of the database
    let companyValues = getCompanyValuesFromGeneralEval(testCompanyID);
    
    // If no company values found, use defaults for testing
    if (!companyValues || companyValues.length === 0) {
      
      // Create some default generic company values for testing
      companyValues = getDefaultCompanyValues();
      
      // Check if we should still use these default values or just proceed
      const useDefaultValues = false; // Set to true to test with default values
      
      if (!useDefaultValues) {
        return { 
          valid: true, 
          message: 'No company values found to validate against.',
          apiStatus: 'no_values',
          openAIStatus: openAIStatus
        };
      }
    }

    // Format the values for OpenAI to understand
    const valuesText = companyValues.map((value, index) => 
      `${index + 1}. ${value.valueDesc}`
    ).join('\n');

    // Prepare the input for AI validation
    const validationResult = await validateWithAI(lead.title, valuesText);
    */
    
    // NEW CONTENT FILTERING - Check for obscene/swearing and senseless posts
    
    // Prepare the input for AI content filtering
    const validationResult = await validateContentStandards(lead.title);
    
    // Add OpenAI status to the result
    return {
      ...validationResult,
      openAIStatus: openAIStatus
    };
  } catch (error) {
    // In case of error, default to allowing the lead to proceed
    return { 
      valid: true, 
      message: 'Error during validation, proceeding with submission.',
      apiStatus: 'error',
      errorDetails: error.message,
      openAIStatus: { working: false, error: error.message }
    };
  }
};

/**
 * Check if the OpenAI API is working
 * @returns {Promise<Object>} Object with status of OpenAI API
 */
export const checkOpenAIStatus = async () => {
  try {
    
    // Simple test prompt to check if OpenAI is responding
    const testPrompt = "Please respond with 'OK' if you're working properly.";
    
    // Set timeout for the request
    const timeoutDuration = 10000; // 10 seconds
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
    
    // Make request to OpenAI
    const response = await axios.post(`${FB_URL}/m103-chatgpt`, {
      question: testPrompt,
      email: 'openai_status_check@thinklead.app'
    }, { signal: controller.signal });
    
    // Clear the timeout
    clearTimeout(timeoutId);
    
    // Check if we got a valid response - handle both possible response formats
    if ((response.data && response.data.answer) || 
        (response.data && response.data.data) || 
        (response.data && response.data.message === "Success")) {
      return {
        working: true,
        responseTime: Date.now(),
        responseData: response.data
      };
    }
    
    return {
      working: false,
      error: 'No valid response from OpenAI API',
      responseTime: Date.now(),
      responseData: response.data
    };
  } catch (error) {
    return {
      working: false,
      error: error.message,
      responseTime: Date.now()
    };
  }
};

/**
 * Fetch company values from the database
 * @param {string} companyID 
 * @returns {Promise<Array>} - The company values
 */
const getCompanyValues = async (companyID) => {
  try {
    
    // Try to get from Firebase database
    const snapshot = await firebase.database.ref('company-values/' + companyID).once('value');
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      
      // No need to filter by companyID since we're already querying by companyID
      const filteredCompanyValues = Object.values(data).filter(v => 
        v.companyID === companyID || v.companyID === '' || v.companyID === undefined
      );
      
      return filteredCompanyValues;
    }
    

    
    // If no values in database, try the API
    const response = await axios.get(`${FB_URL}/companyvalues/${companyID}`);
    if (response.data && response.data.data) {
      
      const filteredCompanyValues = Object.values(response.data.data).filter(v => 
        v.companyID === companyID || v.companyID === '' || v.companyID === undefined
      );
      
      return filteredCompanyValues;
    }
    
    // As a fallback, try the global 'companyvalues' reference as well
    const globalSnapshot = await firebase.database.ref('companyvalues').once('value');
    
    if (globalSnapshot.exists()) {
      const globalData = globalSnapshot.val();

      
      const filteredGlobalValues = Object.values(globalData).filter(v => 
        v.companyID === companyID || v.companyID === '' || v.companyID === undefined
      );
      

      if (filteredGlobalValues.length > 0) {
        return filteredGlobalValues;
      }
    }
    

    return [];
  } catch (error) {
    return [];
  }
};

/**
 * Get company values from generalEval.json
 * @param {string} companyID - The company ID to get values for
 * @returns {Array} - Array of company values formatted for validation
 */
const getCompanyValuesFromGeneralEval = (companyID) => {
  try {
    
    const companyValues = [];
    
    // Filter generalEval.surveyData by companyID and type "general"
    if (generalEval && generalEval.surveyData) {
      generalEval.surveyData.forEach(item => {
        if (item.companyID === companyID && item.type === "general") {
          // Format the value in a way that's clear for OpenAI to understand
          // For the test company, these are values like "Leading Self", "Leading Others", etc.
          companyValues.push({
            valueID: `${companyID}-${item.title.toLowerCase().replace(/\s+/g, '-')}`,
            valueDesc: `${item.title.trim()} - ${item.desc.replace(/[()]/g, '').trim()}`,
            companyID: companyID
          });
        }
      });
    }
    

    return companyValues;
  } catch (error) {
    return [];
  }
};

/**
 * Validate lead text against content standards (obscene/swearing/senseless)
 * @param {string} leadText - The text content of the lead
 * @returns {Promise<Object>} - Promise resolving to { valid: boolean, message: string }
 */
const validateContentStandards = async (leadText) => {
  try {
    
    // Prepare the prompt for OpenAI content filtering
    const prompt = `
You are a content moderation AI tasked with evaluating if a post is appropriate for a professional leadership platform.

Please analyze the following post and determine if it contains:
1. Obscene or sexually explicit content
2. Profanity, swearing, or vulgar language
3. Senseless or meaningless content (spam, gibberish, or completely irrelevant text)

Post Content:
"${leadText}"

Guidelines:
- Professional leadership content should be meaningful and appropriate
- Mild criticism or negative opinions are acceptable if constructive
- Educational or professional discussions about difficult topics are acceptable
- Focus on filtering out clearly inappropriate, offensive, or meaningless content

Please respond with:
1. A "valid" field with a boolean value (true if acceptable, false if should be filtered)
2. A "message" field explaining your decision
3. If not valid, specify which category applies: "obscene", "profanity", or "senseless"
4. If valid, confirm it meets professional standards
`;



    // Use the existing ChatGPT integration
    const response = await axios.post(`${FB_URL}/m103-chatgpt`, {
      question: prompt,
      email: 'content_validation@thinklead.app'
    });

    // Check both possible response formats
    const aiResponse = response.data.answer || response.data.data;
    
    if (aiResponse) {
      try {
        // Try to parse as JSON if it's in that format
        const jsonResponse = JSON.parse(aiResponse);
        
        return {
          valid: jsonResponse.valid === true,
          message: jsonResponse.message || 'Content has been reviewed for professional standards.',
          contentIssue: jsonResponse.contentIssue || null,
          apiStatus: 'success'
        };
      } catch (e) {
        // If not JSON, perform text-based analysis
        const isValid = aiResponse.toLowerCase().includes('valid: true') || 
                        (!aiResponse.toLowerCase().includes('valid: false') && 
                         !aiResponse.toLowerCase().includes('inappropriate') &&
                         !aiResponse.toLowerCase().includes('obscene') &&
                         !aiResponse.toLowerCase().includes('profanity') &&
                         !aiResponse.toLowerCase().includes('senseless'));
        
        // Extract content issue if mentioned
        let contentIssue = null;
        if (aiResponse.toLowerCase().includes('obscene')) contentIssue = 'obscene';
        else if (aiResponse.toLowerCase().includes('profanity') || aiResponse.toLowerCase().includes('swearing')) contentIssue = 'profanity';
        else if (aiResponse.toLowerCase().includes('senseless') || aiResponse.toLowerCase().includes('meaningless')) contentIssue = 'senseless';
        

        
        return {
          valid: isValid,
          message: aiResponse.slice(0, 500), // Limit message length
          contentIssue,
          apiStatus: 'success_text_analysis'
        };
      }
    }
    
    // Handle case where response.data exists but doesn't have expected fields
    if (response.data && response.data.message === "Success") {
      return {
        valid: true, 
        message: 'Content meets professional standards.',
        apiStatus: 'success_non_standard'
      };
    }
    // Default to valid if we can't parse the response
    return { 
      valid: true, 
      message: 'Unable to analyze content, proceeding with submission.',
      apiStatus: 'no_answer'
    };
  } catch (error) {
    return { 
      valid: true, 
      message: 'Error during content validation, proceeding with submission.',
      apiStatus: 'api_error',
      errorDetails: error.message
    };
  }
};

/**
 * Validate lead text against company values using OpenAI (COMMENTED OUT)
 * @param {string} leadText - The text content of the lead
 * @param {string} companyValues - Formatted string of company values
 * @returns {Promise<Object>} - Promise resolving to { valid: boolean, message: string }
 */
const validateWithAI = async (leadText, companyValues) => {
  /* COMMENTED OUT - Company Values Validation (as requested by client)
  try {
    
    // Prepare the prompt for OpenAI
    const prompt = `
You are tasked with evaluating if a post aligns with a company's values. 

Company Values:
${companyValues}

Post Content:
"${leadText}"

Is this post aligned with at least one of the company values? Please respond with:
1. A "valid" field with a boolean value (true or false)
2. A "message" field with a brief explanation
3. If valid, specify which company value(s) it aligns with
4. If not aligned, suggest how the post could be improved to align with company values
`;

    // Use the existing ChatGPT integration
    const response = await axios.post(`${FB_URL}/m103-chatgpt`, {
      question: prompt,
      email: 'lead_validation@thinklead.app'
    });

    // Check both possible response formats
    const aiResponse = response.data.answer || response.data.data;
    
    if (aiResponse) {
      try {
        // Try to parse as JSON if it's in that format
        const jsonResponse = JSON.parse(aiResponse);
        
        return {
          valid: jsonResponse.valid === true,
          message: jsonResponse.message || 'No message provided by AI.',
          matchingValues: jsonResponse.matchingValues || [],
          apiStatus: 'success'
        };
      } catch (e) {
        // If not JSON, perform text-based analysis
        const isValid = aiResponse.toLowerCase().includes('valid: true') || 
                        !aiResponse.toLowerCase().includes('valid: false');
        
        // Extract matching values if mentioned
        let matchingValues = [];
        const valuesMatch = aiResponse.match(/aligned with values?:?\s*(.+?)(?:\.|$)/i);
        if (valuesMatch && valuesMatch[1]) {
          matchingValues = valuesMatch[1].split(',').map(v => v.trim());
        }
        
        return {
          valid: isValid,
          message: aiResponse.slice(0, 500), // Limit message length
          matchingValues,
          apiStatus: 'success_text_analysis'
        };
      }
    }
    
    // Handle case where response.data exists but doesn't have expected fields
    if (response.data && response.data.message === "Success") {
      return {
        valid: true, 
        message: 'Lead validated successfully.',
        apiStatus: 'success_non_standard'
      };
    }
    // Default to valid if we can't parse the response
    return { 
      valid: true, 
      message: 'Unable to determine alignment with company values.',
      apiStatus: 'no_answer'
    };
  } catch (error) {
    
    return { 
      valid: true, 
      message: 'Error during AI validation, proceeding with submission.',
      apiStatus: 'api_error',
      errorDetails: error.message
    };
  }
  */
  
  // This function has been temporarily disabled as per client request
  return {
    valid: true,
    message: 'Company values validation is temporarily disabled.',
    apiStatus: 'disabled'
  };
};

/**
 * Get default company values for testing when none are found in the database
 * @returns {Array} Array of default company values
 */
const getDefaultCompanyValues = () => {
  return [
    {
      valueID: 'default-integrity',
      valueDesc: 'Integrity: Acting with honesty, ethics, and transparency in all interactions',
      companyID: 'default'
    },
    {
      valueID: 'default-teamwork',
      valueDesc: 'Teamwork: Collaborating effectively with mutual respect and shared goals',
      companyID: 'default'
    },
    {
      valueID: 'default-innovation',
      valueDesc: 'Innovation: Embracing creativity and continuous improvement',
      companyID: 'default'
    },
    {
      valueID: 'default-excellence',
      valueDesc: 'Excellence: Striving for the highest quality in all we do',
      companyID: 'default'
    },
    {
      valueID: 'default-customer',
      valueDesc: 'Customer Focus: Prioritizing customer needs and satisfaction',
      companyID: 'default'
    }
  ];
}; 